import { READING_SCALE } from './moisture-determination.js';
import { meanOfDecimals, ratioToDecimal, readMassRow, readingToDecimal } from './exact-decimal.js';
/**
 * ASPHALT SPECIFIC GRAVITY DETERMINATIONS — ASTM D2726 (Gmb) and ASTM D2041
 * (Gmm), decisions D260–D266.
 *
 * Every formula is traced to the laboratory's reference workbook
 * `asphalt.xlsx`, sheet `2` (docs/REFERENCE_TEST_AUDIT.md sections 3.17 and
 * 3.18), and nothing else is calculated here: no air voids, VMA, VFA or any
 * other volumetric value, no limit and no pass/fail.
 *
 *   ASTM D2726, three specimen columns Q, W, AC:
 *     Volume  = B - C              (Q22 `=Q20-Q21`,          format 0.0)
 *     Gmb     = A / (B - C)        (Q23 `=Q19/(Q20-Q21)`,    format 0.000)
 *     Average                      (Q24 `=SUM(Q23:AH23)/3`,  format 0.000)
 *
 *   ASTM D2041, two determination columns BE, BK:
 *     Gmm     = A / (A + D - E)    (BE23 `=BE20/(BE20+BE21-BE22)`, format 0.000)
 *     Average                      (BE24 `=(BE23+BK23)/2`,         format 0.000)
 *
 * THE AVERAGE (D262). The workbook divides by its fixed column count; the
 * owner's rule is "the average of the valid results". The two agree whenever
 * every column is filled, which is the only case the workbook supports; a
 * submission is refused while any row is incomplete or impossible, so a
 * submitted average always covers every recorded row. The average is taken
 * of the stored, UNROUNDED ratios, as the workbook averages its unrounded
 * cells, and only shaped to three places for display.
 *
 * A reading is never corrected. An incomplete or impossible row is reported
 * with its exact field and a stable code, and is not calculated.
 */
/** Decimal places a stored ratio (Gmb, Gmm and their averages) is carried to. */
export const SPECIFIC_GRAVITY_SCALE = 10;
/**
 * DISPLAY precision, from the workbook's number formats — display only.
 * Masses are General in the workbook, so they are shown exactly as entered.
 */
export const SPECIFIC_GRAVITY_DISPLAY_PLACES = Object.freeze({
    /** Q22:AC22 `0.0`. */
    volume: 1,
    /** Q23:AC23, Q24, BE23:BK23, BE24 `0.000`. */
    specificGravity: 3,
});
export const SPECIFIC_GRAVITY_ISSUE_CODE = Object.freeze({
    READING_REQUIRED: 'reading_required',
    NOT_A_DECIMAL: 'not_a_decimal',
    NEGATIVE_MASS: 'negative_mass',
    /** D2726: B - C, the volume and the denominator, would be zero or negative. */
    VOLUME_NOT_POSITIVE: 'volume_not_positive',
    /** D2041: A + D - E, the denominator, would be zero or negative. */
    DENOMINATOR_NOT_POSITIVE: 'denominator_not_positive',
});
export const ROW_STATUS = Object.freeze({
    /** Every reading present, numeric and usable: calculated. */
    COMPLETE: 'complete',
    /** At least one reading missing, none wrong: not calculated. */
    INCOMPLETE: 'incomplete',
    /** At least one reading wrong or impossible: not calculated. */
    INVALID: 'invalid',
});
function settle(issues, missing, calculate) {
    if (issues.some((issue) => issue.code !== SPECIFIC_GRAVITY_ISSUE_CODE.READING_REQUIRED)) {
        return { status: ROW_STATUS.INVALID, issues, calculation: null };
    }
    const calculation = missing ? null : calculate();
    if (calculation === null)
        return { status: ROW_STATUS.INCOMPLETE, issues, calculation: null };
    return { status: ROW_STATUS.COMPLETE, issues: [], calculation };
}
// ---------------------------------------------------------------------------
// ASTM D2726 — one specimen
// ---------------------------------------------------------------------------
/** The three readings, in the order the workbook lists them (rows 19–21). */
export const D2726_READING_FIELDS = Object.freeze(['dryMassInAir', 'ssdMassInAir', 'massInWater']);
export function evaluateD2726Specimen(readings) {
    const { values, issues, missing } = readMassRow(D2726_READING_FIELDS, readings);
    const all = [...issues];
    const a = values.get('dryMassInAir');
    const b = values.get('ssdMassInAir');
    const c = values.get('massInWater');
    // Named whenever both readings exist, even while A is still missing.
    if (b !== undefined && c !== undefined && b - c <= 0n) {
        all.push({ field: 'massInWater', code: SPECIFIC_GRAVITY_ISSUE_CODE.VOLUME_NOT_POSITIVE });
    }
    return settle(all, missing, () => {
        if (a === undefined || b === undefined || c === undefined)
            return null;
        const volume = b - c;
        return {
            volume: readingToDecimal(volume),
            // A and (B - C) share READING_SCALE places, which cancel.
            bulkSpecificGravity: ratioToDecimal(a, volume, SPECIFIC_GRAVITY_SCALE),
        };
    });
}
// ---------------------------------------------------------------------------
// ASTM D2041 — one determination
// ---------------------------------------------------------------------------
/** The three readings, in the order the workbook lists them (rows 20–22). */
export const D2041_READING_FIELDS = Object.freeze([
    'dryMassInAir',
    'containerWaterMass',
    'containerSampleWaterMass',
]);
export function evaluateD2041Determination(readings) {
    const { values, issues, missing } = readMassRow(D2041_READING_FIELDS, readings);
    const all = [...issues];
    const a = values.get('dryMassInAir');
    const d = values.get('containerWaterMass');
    const e = values.get('containerSampleWaterMass');
    if (a !== undefined && d !== undefined && e !== undefined && a + d - e <= 0n) {
        all.push({ field: 'containerSampleWaterMass', code: SPECIFIC_GRAVITY_ISSUE_CODE.DENOMINATOR_NOT_POSITIVE });
    }
    return settle(all, missing, () => {
        if (a === undefined || d === undefined || e === undefined)
            return null;
        return { maximumSpecificGravity: ratioToDecimal(a, a + d - e, SPECIFIC_GRAVITY_SCALE) };
    });
}
/**
 * The average of the CALCULATED rows' ratios (D262), or null when none is
 * calculated. Rows that are incomplete or impossible are not averaged.
 */
export function averageSpecificGravity(ratios) {
    const present = ratios.filter((value) => typeof value === 'string');
    return meanOfDecimals(present, SPECIFIC_GRAVITY_SCALE);
}
/** Readings carry this many places; re-exported for forms and tests. */
export const SPECIFIC_GRAVITY_READING_SCALE = READING_SCALE;
//# sourceMappingURL=asphalt-specific-gravity.js.map